import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { exportElementToPDF } from "@/lib/pdfExport";
import {
  format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths,
  eachDayOfInterval, getDay
} from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Clock, Pencil, Trash2, History, ChevronLeft, ChevronRight, FileDown, Calendar, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const API = "/api";
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function TimeManagement() {
  const { getAuthHeader } = useAuth();
  const dtrRef = useRef(null);
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editData, setEditData] = useState({ clock_in: "", clock_out: "", admin_note: "" });
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedTutor, setSelectedTutor] = useState("all");
  const [tutorSearch, setTutorSearch] = useState("");
  const [tutorDropdownOpen, setTutorDropdownOpen] = useState(false);

  const monthStartStr = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
  const monthEndStr = format(endOfMonth(selectedMonth), "yyyy-MM-dd");
  const monthLabel = format(selectedMonth, "MMMM yyyy");
  const monthStart = startOfMonth(selectedMonth);
  const monthEnd = endOfMonth(selectedMonth);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      setUsers(response.data.filter(u => u.role === "tutor"));
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, [getAuthHeader]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/time-clock/all-records?start_date=${monthStartStr}&end_date=${monthEndStr}`;
      if (selectedTutor !== "all") url += `&user_id=${selectedTutor}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setRecords(response.data.records || []);
    } catch (error) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, monthStartStr, monthEndStr, selectedTutor]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const goToPrevMonth = () => setSelectedMonth(prev => subMonths(prev, 1));
  const goToNextMonth = () => setSelectedMonth(prev => addMonths(prev, 1));

  // Build DTR: one row per day of the month, fill in clock data
  const buildDTR = () => {
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    return days.map(day => {
      const dateStr = format(day, "yyyy-MM-dd");
      const dayRecords = records.filter(r => {
        if (!r.clock_in) return false;
        return format(parseISO(r.clock_in), "yyyy-MM-dd") === dateStr;
      });
      const totalHours = dayRecords.reduce((sum, r) => sum + (r.total_hours || 0), 0);
      return {
        date: day,
        dateStr,
        dayName: DAY_NAMES[getDay(day)],
        records: dayRecords,
        clockIn: dayRecords.length > 0 ? dayRecords[0].clock_in : null,
        clockOut: dayRecords.length > 0 ? dayRecords[dayRecords.length - 1].clock_out : null,
        totalHours: totalHours > 0 ? totalHours.toFixed(2) : "",
        hasData: dayRecords.length > 0,
      };
    });
  };

  const dtr = buildDTR();
  const grandTotalHours = dtr.reduce((sum, d) => sum + (parseFloat(d.totalHours) || 0), 0);
  const totalDaysWorked = dtr.filter(d => d.hasData).length;
  const selectedTutorName = selectedTutor === "all" ? "All Tutors" : users.find(u => u.id === selectedTutor)?.name || "Unknown";

  const formatTime = (isoString) => {
    if (!isoString) return "";
    return format(parseISO(isoString), "hh:mm a");
  };

  const handleExportPDF = async () => {
    if (!dtrRef.current) return;
    try {
      toast.info("Generating DTR PDF...");
      await exportElementToPDF(
        dtrRef.current,
        `DTR_${selectedTutorName.replace(/\s+/g, "_")}_${format(selectedMonth, "MMM_yyyy")}.pdf`,
        { orientation: "portrait" }
      );
      toast.success("DTR PDF downloaded!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setEditData({
      clock_in: record.clock_in ? record.clock_in.slice(0, 16) : "",
      clock_out: record.clock_out ? record.clock_out.slice(0, 16) : "",
      admin_note: ""
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedRecord) return;
    try {
      const payload = { admin_note: editData.admin_note };
      if (editData.clock_in) payload.clock_in = new Date(editData.clock_in).toISOString();
      if (editData.clock_out) payload.clock_out = new Date(editData.clock_out).toISOString();
      await axios.put(`${API}/time-clock/records/${selectedRecord.id}`, payload, { headers: getAuthHeader() });
      toast.success("Record updated");
      setEditDialogOpen(false);
      fetchRecords();
    } catch (error) {
      toast.error("Failed to update record");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await axios.delete(`${API}/time-clock/records/${recordId}`, { headers: getAuthHeader() });
      toast.success("Record deleted");
      fetchRecords();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  return (
    <div className="space-y-6" data-testid="time-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Daily Time Record (DTR)</h1>
          <p className="text-slate-500">Monthly attendance records for tutors</p>
        </div>
        <Button onClick={handleExportPDF} variant="outline" data-testid="export-dtr-btn">
          <FileDown className="w-4 h-4 mr-2" />
          Export DTR
        </Button>
      </div>

      {/* Controls: Month Nav + Tutor Selector */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {/* Month Navigation */}
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1">
          <Button variant="ghost" size="sm" onClick={goToPrevMonth} data-testid="prev-month-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 px-4 min-w-[180px] justify-center">
            <Calendar className="w-4 h-4 text-indigo-600" />
            <span className="text-base font-semibold text-slate-800" data-testid="month-label">{monthLabel}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={goToNextMonth} data-testid="next-month-btn">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Tutor Selector with Search */}
        <Popover open={tutorDropdownOpen} onOpenChange={setTutorDropdownOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-64 justify-between font-normal" data-testid="tutor-filter">
              <span className="truncate">{selectedTutorName}</span>
              <ChevronRight className="w-4 h-4 ml-2 opacity-50 rotate-90 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search tutor..."
                  value={tutorSearch}
                  onChange={(e) => setTutorSearch(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="tutor-search-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto p-1">
              <button
                className={cn("w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-100", selectedTutor === "all" && "bg-indigo-50 text-indigo-700 font-medium")}
                onClick={() => { setSelectedTutor("all"); setTutorDropdownOpen(false); setTutorSearch(""); }}
              >
                All Tutors
              </button>
              {users
                .filter(u => u.name.toLowerCase().includes(tutorSearch.toLowerCase()))
                .map(u => (
                  <button
                    key={u.id}
                    className={cn("w-full text-left px-3 py-2 text-sm rounded hover:bg-slate-100", selectedTutor === u.id && "bg-indigo-50 text-indigo-700 font-medium")}
                    onClick={() => { setSelectedTutor(u.id); setTutorDropdownOpen(false); setTutorSearch(""); }}
                  >
                    {u.name}
                  </button>
                ))}
              {users.filter(u => u.name.toLowerCase().includes(tutorSearch.toLowerCase())).length === 0 && tutorSearch && (
                <p className="px-3 py-2 text-sm text-slate-400">No tutors found</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* DTR Table (this ref is captured for PDF export) */}
      <div ref={dtrRef} className="bg-white">
        {/* DTR Header — visible in PDF */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="text-center mb-1">
            <h2 className="text-lg font-bold text-slate-900">DAILY TIME RECORD (DTR)</h2>
            <p className="text-sm text-slate-600">{selectedTutorName}</p>
            <p className="text-sm text-slate-500">Period: {format(monthStart, "MMMM d")} - {format(monthEnd, "MMMM d, yyyy")}</p>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm rounded-none border-x-0 border-t-0">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="font-bold text-slate-800 w-28">Date</TableHead>
                  <TableHead className="font-bold text-slate-800 w-28">Day</TableHead>
                  {selectedTutor === "all" && <TableHead className="font-bold text-slate-800">Tutor</TableHead>}
                  <TableHead className="font-bold text-slate-800 text-center w-28">Clock In</TableHead>
                  <TableHead className="font-bold text-slate-800 text-center w-28">Clock Out</TableHead>
                  <TableHead className="font-bold text-slate-800 text-center w-24">Total Hours</TableHead>
                  <TableHead className="font-bold text-slate-800 text-center w-20 print-hide">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-400">Loading...</TableCell>
                  </TableRow>
                ) : (
                  dtr.map((day) => {
                    const isSunday = day.dayName === "Sunday";
                    const isSaturday = day.dayName === "Saturday";
                    const isWeekend = isSunday || isSaturday;

                    if (selectedTutor !== "all") {
                      // Single tutor view: one row per day
                      return (
                        <TableRow
                          key={day.dateStr}
                          className={cn(
                            isWeekend && "bg-slate-50",
                            day.hasData && "bg-green-50/40"
                          )}
                          data-testid={`dtr-row-${day.dateStr}`}
                        >
                          <TableCell className="font-medium text-slate-700">{format(day.date, "MMM dd")}</TableCell>
                          <TableCell className={cn("text-sm", isWeekend ? "text-slate-400 italic" : "text-slate-600")}>
                            {day.dayName}
                          </TableCell>
                          <TableCell className="text-center text-sm">{formatTime(day.clockIn)}</TableCell>
                          <TableCell className="text-center text-sm">
                            {day.clockOut ? formatTime(day.clockOut) : (day.hasData ? <span className="text-green-600 text-xs">Working...</span> : "")}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-sm">{day.totalHours}</TableCell>
                          <TableCell className="text-center print-hide">
                            {day.records.length > 0 && (
                              <div className="flex items-center justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEditRecord(day.records[0])} className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteRecord(day.records[0].id)} className="h-7 w-7 p-0 text-slate-500 hover:text-red-600">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // All tutors view: show records grouped by day
                      if (!day.hasData) return null;
                      return day.records.map((record, idx) => (
                        <TableRow
                          key={`${day.dateStr}-${record.id}`}
                          className={cn(isWeekend && "bg-slate-50")}
                          data-testid={`dtr-row-${record.id}`}
                        >
                          {idx === 0 && (
                            <>
                              <TableCell rowSpan={day.records.length} className="font-medium text-slate-700 align-top">{format(day.date, "MMM dd")}</TableCell>
                              <TableCell rowSpan={day.records.length} className={cn("text-sm align-top", isWeekend ? "text-slate-400 italic" : "text-slate-600")}>{day.dayName}</TableCell>
                            </>
                          )}
                          <TableCell className="text-sm font-medium">{record.user_name}</TableCell>
                          <TableCell className="text-center text-sm">{formatTime(record.clock_in)}</TableCell>
                          <TableCell className="text-center text-sm">
                            {record.clock_out ? formatTime(record.clock_out) : <span className="text-green-600 text-xs">Working...</span>}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-sm">{record.total_hours ? `${record.total_hours}` : ""}</TableCell>
                          <TableCell className="text-center print-hide">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEditRecord(record)} className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteRecord(record.id)} className="h-7 w-7 p-0 text-slate-500 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Summary Footer — visible in PDF */}
        {!loading && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-slate-600">
                <span className="font-semibold">Days Worked:</span> {totalDaysWorked}
              </div>
              <div className="text-base font-bold text-slate-900">
                Total Hours: {grandTotalHours.toFixed(2)}h
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="edit-record-desc">
          <DialogHeader>
            <DialogTitle>Edit Time Record</DialogTitle>
            <p id="edit-record-desc" className="text-sm text-slate-500">Editing record for {selectedRecord?.user_name}</p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Clock In</Label>
              <Input type="datetime-local" value={editData.clock_in} onChange={(e) => setEditData(prev => ({ ...prev, clock_in: e.target.value }))} data-testid="edit-clock-in" />
            </div>
            <div className="space-y-2">
              <Label>Clock Out</Label>
              <Input type="datetime-local" value={editData.clock_out} onChange={(e) => setEditData(prev => ({ ...prev, clock_out: e.target.value }))} data-testid="edit-clock-out" />
            </div>
            <div className="space-y-2">
              <Label>Admin Note (required for audit)</Label>
              <Textarea value={editData.admin_note} onChange={(e) => setEditData(prev => ({ ...prev, admin_note: e.target.value }))} placeholder="Reason for edit..." data-testid="edit-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="history-desc">
          <DialogHeader>
            <DialogTitle>Edit History</DialogTitle>
            <p id="history-desc" className="text-sm text-slate-500">Record changes for {selectedRecord?.user_name}</p>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-3">
            {selectedRecord?.edit_history?.length > 0 ? (
              selectedRecord.edit_history.map((h, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Edited by: {h.edited_by_name}</span>
                    <span>{format(parseISO(h.edited_at), "MMM dd, yyyy HH:mm")}</span>
                  </div>
                  <div className="mt-2 text-slate-500">
                    <p>Previous In: {formatTime(h.previous_clock_in)}</p>
                    <p>Previous Out: {h.previous_clock_out ? formatTime(h.previous_clock_out) : "-"}</p>
                  </div>
                  {h.note && <p className="mt-2 text-slate-700">Note: {h.note}</p>}
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-4">No edit history</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
